/**
 * usePaywallData - Hook para buscar dados dinâmicos de paywalls
 * 
 * Busca informações de planos e add-ons diretamente do banco de dados
 * para exibição em telas de paywall (Campanhas, Templates, etc.)
 * Garante que preços e features sejam sempre atualizados conforme configuração admin.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Interface para dados do plano
interface PlanData {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number | null;
  description: string | null;
  features: string[] | null;
}

// Interface para dados do add-on
interface AddonData {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  setup_fee: number;
}

/**
 * Hook que busca dados de planos e add-ons para exibição em paywalls
 * Retorna dados do plano que inclui campanhas, add-on de campanhas e plano Enterprise
 */
export function usePaywallData() {
  // Busca plano que inclui campanhas (menor preço entre os que têm has_campaigns = true)
  const { data: campaignPlan, isLoading: isLoadingCampaignPlan } = useQuery({
    queryKey: ['paywall-campaign-plan'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, name, price_monthly, price_yearly, description, features')
        .eq('has_campaigns', true)
        .eq('is_active', true)
        .order('price_monthly', { ascending: true })
        .limit(1)
        .single();
      
      if (error) {
        console.error('[usePaywallData] Erro ao buscar plano com campanhas:', error);
        return null;
      }
      
      return data as PlanData;
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Busca add-on de campanhas
  const { data: campaignAddon, isLoading: isLoadingCampaignAddon } = useQuery({
    queryKey: ['paywall-campaign-addon'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plan_addons')
        .select('id, name, slug, description, price_monthly, price_yearly, setup_fee')
        .eq('slug', 'campaigns_module')
        .eq('is_active', true)
        .single();
      
      if (error) {
        console.error('[usePaywallData] Erro ao buscar add-on de campanhas:', error);
        return null;
      }
      
      return data as AddonData;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Busca plano Enterprise (para Templates/WhatsApp Oficial)
  const { data: enterprisePlan, isLoading: isLoadingEnterprise } = useQuery({
    queryKey: ['paywall-enterprise-plan'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, name, price_monthly, price_yearly, description, features')
        .eq('id', 'enterprise')
        .single();
      
      if (error) {
        console.error('[usePaywallData] Erro ao buscar plano Enterprise:', error);
        return null;
      }
      
      return data as PlanData;
    },
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Formata valor monetário para exibição em BRL
   * Ex: 149.90 -> "149,90"
   */
  const formatPrice = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '0,00';
    return value.toFixed(2).replace('.', ',');
  };

  return {
    // Dados do plano que inclui campanhas
    campaignPlan,
    // Dados do add-on de campanhas
    campaignAddon,
    // Dados do plano Enterprise
    enterprisePlan,
    // Estado de carregamento
    isLoading: isLoadingCampaignPlan || isLoadingCampaignAddon || isLoadingEnterprise,
    // Utilitário para formatação de preço
    formatPrice,
  };
}
